import { FC, RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  ChevronIcon,
  CloseIcon,
  ConfiguredCalendar,
  Dropdown,
  DropdownChangeEvent,
  InputText,
  PencilIcon,
  Sidebar,
  UserAvatar,
  useGlobalToast,
} from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import {
  selectCaseIdData,
  selectCaseMetadata,
  selectCasesList,
  selectUserAboutData,
  selectUsersListData,
  useAppSelector,
} from '@/store';
import {
  AllegationDocumentIds,
  CasesFiltersStatusOptions,
  InstructingCompany,
  isEndpointLockError,
  useGetListInstructingCompaniesQuery,
  useLazyGetCaseAllegationDownloadQuery,
  useLazyGetCaseQuery,
  useLazyPostCaseAllegationDeleteQuery,
  useLazyPostCaseAllegationGetUploadQuery,
  useLazyPostCaseCreateCancelQuery,
  useLazyPostCaseCreateQuery,
  useLazyPostCaseEditQuery,
  usePostCaseInfoEditMutation,
} from '@/api';
import { AssignUserPopup } from '@/ui/components/AssignUserPopup';
import { CaseBackgroundSection, CaseBackgroundDocItem } from '@/ui/components/CaseBackgroundSection';
import { GreenButton } from '@/ui/components/GreenButton';
import { InfoTooltipIcon } from '@/ui/components/InfoTooltipIcon';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { ToolbarChipButton } from '@/ui/components/ToolbarChipButton';
import type { CaseEditProps } from '@/api/case/caseEdit';
import { format } from 'date-fns';
import { deepEqual } from '@/utils';
import { useAzureFileUpload } from '@repo/file-uploader';
import { useLocation, useNavigate } from 'react-router';
import { AppRoutes, NAV_HEIGHT_PX, SIDEBAR_PANEL_SHADOW } from '@/constants';
import { themedDropdownPt } from '@/ui/components/themedDropdownPt';
import {
  CreateEditCaseSidebarFormData,
  CreateEditCaseSidebarFormDataFieldsName,
  productTypeOptions,
  useCreateEditCaseForm,
} from '../hooks';

const allowedCaseReferenceInputSymbolsRegex = /^[A-Z0-9.,/\-_()]*$/;

interface CreateEditCaseSidebarProps {
  closeSidebar: () => void;
}

export const CreateEditCaseSidebar: FC<CreateEditCaseSidebarProps> = ({ closeSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const caseId = useAppSelector(selectCaseIdData);
  const usersList = useAppSelector(selectUsersListData);
  const casesList = useAppSelector(selectCasesList);
  const caseMetadata = useAppSelector(selectCaseMetadata);
  const currentUser = useAppSelector(selectUserAboutData);
  const globalToastRef = useGlobalToast();

  const { uploadList, uploadFile, /*deleteFile,*/ deleteUpload, cancelUpload, downloadFile } = useAzureFileUpload();
  const [uploadedFilesList, setUploadedFilesList] = useState<AllegationDocumentIds[]>([]);
  const [initEditFormData, setInitEditFormData] = useState<CreateEditCaseSidebarFormData | null>(null);

  // Only MLS-org users can view / assign an instructing company. Skip
  // the network round-trip otherwise — non-MLS users never see the field.
  const isMlsUser = !!currentUser?.isMls;
  const { data: instructingCompaniesData } = useGetListInstructingCompaniesQuery(undefined, { skip: !isMlsUser });
  const instructingCompanies = useMemo(() => instructingCompaniesData?.companies ?? [], [instructingCompaniesData]);

  // The case may already have an instructing company saved — in which
  // case the BE returns both `instructingCompanyId` and
  // `instructingCompanyName` on getCase / cases. Surface that as a
  // synthetic {id, name} option so the dropdown can render the saved
  // selection (a) before the list-instructing-companies query resolves,
  // and (b) even if that list no longer contains this id (e.g. the
  // company was later removed from the org's active list). We prefer
  // casesList over caseMetadata to match the hydration priority in the
  // useLayoutEffect below.
  const savedInstructingCompany = useMemo<InstructingCompany | null>(() => {
    if (!caseId) return null;
    const fromList = casesList.find((c) => c.caseId === caseId);
    if (fromList?.instructingCompanyId) {
      return {
        instructingCompanyId: fromList.instructingCompanyId,
        name: fromList.instructingCompanyName ?? fromList.instructingCompanyId,
      };
    }
    if (caseMetadata?.instructingCompanyId) {
      return {
        instructingCompanyId: caseMetadata.instructingCompanyId,
        name: caseMetadata.instructingCompanyName ?? caseMetadata.instructingCompanyId,
      };
    }
    return null;
  }, [caseId, casesList, caseMetadata]);

  // Merge the saved company into the dropdown options list when it
  // isn't already present in the API list. The API entry wins on id
  // collision (it carries the canonical name), so we only prepend the
  // synthetic option when there's no match — otherwise the dropdown's
  // value lookup (also keyed on id) will resolve to the API row.
  const mergedInstructingCompanies = useMemo<InstructingCompany[]>(() => {
    if (!savedInstructingCompany) return instructingCompanies;
    const alreadyInList = instructingCompanies.some(
      (c) => c.instructingCompanyId === savedInstructingCompany.instructingCompanyId,
    );
    if (alreadyInList) return instructingCompanies;
    return [savedInstructingCompany, ...instructingCompanies];
  }, [instructingCompanies, savedInstructingCompany]);

  const [triggerPostCaseAllegationGetUpload] = useLazyPostCaseAllegationGetUploadQuery();
  const [triggerPostCaseAllegationDelete] = useLazyPostCaseAllegationDeleteQuery();
  // Counted rather than read from the query, which only follows the latest delete when several overlap
  const [pendingAllegationDeletes, setPendingAllegationDeletes] = useState(0);
  // Case-scoped: one request returns signed download URLs for every
  // allegation doc on this case. We fire it lazily on the first download
  // click; subsequent clicks reuse the cached response for the same caseId
  // via RTK Query's default request dedupe.
  const [triggerGetCaseAllegationDownload] = useLazyGetCaseAllegationDownloadQuery();
  const [triggerPostCaseCreate] = useLazyPostCaseCreateQuery();
  const [triggerPostCaseEdit] = useLazyPostCaseEditQuery();
  const [triggerPostCaseCreateCancel] = useLazyPostCaseCreateCancelQuery();
  // Used to fetch `additionalInfo` (clientName, clientDob, etc.) which the
  // store-backed casesList / caseMetadata don't carry. The initial result
  // hydrates the Optional-fields client name + DOB inputs in edit mode.
  const [triggerGetCase, { data: caseFullData }] = useLazyGetCaseQuery();
  // Persists the `additionalInfo` fields. Note: this endpoint takes them
  // flat in the body (no wrapper) — see caseInfoEdit.ts. `gRef` is sent
  // through the existing case/edit instead, same split as ActionsPanel.
  const [triggerPostCaseInfoEdit] = usePostCaseInfoEditMutation();

  const { formData, setFormData, handleValueChange, canSubmit } = useCreateEditCaseForm();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [caseStatus, setCaseStatus] = useState<string>(CasesFiltersStatusOptions.NEW);

  // Anchor rect used to position AssignUserPopup. Null while the popup is
  // closed; populated with the trigger's bounding rect on click so the popup
  // can decide whether to flip above the trigger when there's no room below.
  const assignUserTriggerRef = useRef<HTMLButtonElement>(null);
  const [assignUserAnchor, setAssignUserAnchor] = useState<{ top: number; bottom: number; left: number } | null>(null);
  // Drives the avatar→pencil swap on hover. Same pattern as the V2 case-info
  // sidebar's UserField — the pencil chip signals editability without needing
  // a separate visible "edit" affordance next to the row.
  const [isAssignUserHovered, setIsAssignUserHovered] = useState(false);
  // "Advanced fields" accordion (Additional reference, Due date, Urgent).
  // Collapsed by default to keep the sidebar compact; users with extra
  // metadata to set can expand it inline.
  const [isAdvancedFieldsOpen, setIsAdvancedFieldsOpen] = useState(false);

  const handleOpenAssignUserPopup = () => {
    if (!assignUserTriggerRef.current) return;
    const rect = assignUserTriggerRef.current.getBoundingClientRect();
    setAssignUserAnchor({ top: rect.top, bottom: rect.bottom, left: rect.left });
  };
  const handleCloseAssignUserPopup = () => setAssignUserAnchor(null);

  // Tracks whether we've already hydrated edit-mode data from
  // casesList/caseMetadata. Once true, the autopopulate-current-user
  // effect must not run — even if the form's assignedUser is somehow null
  // again — because that would clobber a real assignee with the current
  // user. Set inside useLayoutEffect below the moment edit hydration
  // completes (regardless of whether the assignedUser lookup succeeded).
  const hasHydratedEditDataRef = useRef(false);
  // Same idea for the lazily-fetched `additionalInfo` (clientName /
  // clientDob). The fetch returns later than the case-list hydration, so
  // it has its own pristine flag — once we've written its values once,
  // subsequent refetches must not overwrite mid-flight user edits.
  const hasHydratedAdditionalInfoRef = useRef(false);
  // Set when the user explicitly clears the assignee via the "x" button.
  // The autopopulate-current-user effect below otherwise sees the null
  // value and immediately re-fills it with currentUser, making the clear
  // appear to flicker back. Once true, that effect is suppressed for the
  // remaining lifetime of the sidebar.
  const hasUserClearedAssigneeRef = useRef(false);

  // Fetch the full case (info + additionalInfo) when the sidebar opens
  // for an existing case. casesList / caseMetadata don't carry
  // additionalInfo, so without this we'd never see clientName / clientDob.
  useEffect(() => {
    if (!caseId) return;
    triggerGetCase({ caseId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Hydrate the Optional-fields client name + DOB once the lazy query
  // returns. Runs only while pristine so a slow refetch can't clobber
  // in-flight edits.
  useEffect(() => {
    if (hasHydratedAdditionalInfoRef.current) return;
    if (!caseFullData) return;
    const ai = caseFullData.additionalInfo;
    if (!ai) return;
    hasHydratedAdditionalInfoRef.current = true;
    const dobDate = ai.clientDob ? new Date(ai.clientDob) : null;
    setFormData((prev) => ({
      ...prev,
      [CreateEditCaseSidebarFormDataFieldsName.ClientName]: ai.clientName ?? '',
      [CreateEditCaseSidebarFormDataFieldsName.ClientDob]: dobDate && !isNaN(dobDate.getTime()) ? dobDate : null,
    }));
    setInitEditFormData((prev) =>
      prev
        ? {
            ...prev,
            [CreateEditCaseSidebarFormDataFieldsName.ClientName]: ai.clientName ?? '',
            [CreateEditCaseSidebarFormDataFieldsName.ClientDob]: dobDate && !isNaN(dobDate.getTime()) ? dobDate : null,
          }
        : prev,
    );
  }, [caseFullData, setFormData]);

  useLayoutEffect(() => {
    // Hydrate edit-mode form fields exactly once. The lazy getCase
    // fetched above dispatches setCaseMetadata when it resolves, so
    // caseMetadata changing is a normal mid-edit event — without this
    // guard a late response would re-run the patch and overwrite
    // fields the user has already started editing. The additionalInfo
    // (clientName / clientDob) hydration lives in its own effect with
    // a separate `hasHydratedAdditionalInfoRef` flag.
    if (hasHydratedEditDataRef.current) return;
    if (caseId) {
      const caseItem = casesList.find((item) => item.caseId === caseId);

      if (caseItem) {
        hasHydratedEditDataRef.current = true;
        const assignedUser = caseItem.assignedTo
          ? usersList.find((user) => user.userId === caseItem.assignedTo?.userId) || null
          : null;

        const selectedProductType =
          productTypeOptions.find((option) => option.code === caseItem.paginationProduct) || productTypeOptions[0];

        // Patch only the case/edit-owned fields. ClientName / ClientDob
        // come from a separate endpoint (case-info-edit) and are
        // populated by the additionalInfo hydration effect — preserve
        // them across re-runs so a refetch after save doesn't blank
        // them in the UI.
        const editPatch = {
          [CreateEditCaseSidebarFormDataFieldsName.CaseReference]: caseItem.caseReference,
          [CreateEditCaseSidebarFormDataFieldsName.GReference]: caseItem.gRef || '',
          [CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]: caseItem.allegationDocuments?.map(
            (c) => c.allegationDocumentId,
          ),
          [CreateEditCaseSidebarFormDataFieldsName.AssignedUser]: assignedUser,
          [CreateEditCaseSidebarFormDataFieldsName.DueDate]: caseItem.dueDate ? new Date(caseItem.dueDate) : new Date(),
          [CreateEditCaseSidebarFormDataFieldsName.Urgent]: caseItem.isUrgent,
          [CreateEditCaseSidebarFormDataFieldsName.PaginationProduct]: selectedProductType,
          [CreateEditCaseSidebarFormDataFieldsName.InstructingCompany]: caseItem.instructingCompanyId ?? null,
        };

        setFormData((prev) => ({ ...prev, ...editPatch }));
        setInitEditFormData((prev) => ({
          ...editPatch,
          [CreateEditCaseSidebarFormDataFieldsName.ClientName]:
            prev?.[CreateEditCaseSidebarFormDataFieldsName.ClientName] ?? '',
          [CreateEditCaseSidebarFormDataFieldsName.ClientDob]:
            prev?.[CreateEditCaseSidebarFormDataFieldsName.ClientDob] ?? null,
        }));

        setUploadedFilesList(caseItem.allegationDocuments);
        setCaseStatus(caseItem.status);
        setIsEditMode(true);
      }

      // If caseItem is not found, but caseMetadata exists, use it to initialize the form (with defaults for missing fields)
      // Only use fields that exist in CaseMetadata, set defaults for the rest
      if (!caseItem && caseMetadata) {
        hasHydratedEditDataRef.current = true;
        const assignedUser = caseMetadata.assignedTo
          ? usersList.find((user) => user.userId === caseMetadata.assignedTo.userId) || null
          : null;

        const selectedProductType =
          productTypeOptions.find((option) => option.code === caseMetadata.paginationProduct) || productTypeOptions[0];

        const editPatch = {
          [CreateEditCaseSidebarFormDataFieldsName.CaseReference]: caseMetadata.caseReference || '',
          [CreateEditCaseSidebarFormDataFieldsName.GReference]: caseMetadata.gRef || '',
          [CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]: caseMetadata.allegationDocuments?.map(
            (c) => c.allegationDocumentId,
          ),
          [CreateEditCaseSidebarFormDataFieldsName.AssignedUser]: assignedUser,
          [CreateEditCaseSidebarFormDataFieldsName.DueDate]: caseMetadata.dueDate
            ? new Date(caseMetadata.dueDate)
            : new Date(),
          [CreateEditCaseSidebarFormDataFieldsName.Urgent]:
            typeof caseMetadata.isUrgent === 'boolean' ? caseMetadata.isUrgent : false,
          [CreateEditCaseSidebarFormDataFieldsName.PaginationProduct]: selectedProductType,
          [CreateEditCaseSidebarFormDataFieldsName.InstructingCompany]: caseMetadata.instructingCompanyId ?? null,
        };

        // Same pattern as the casesList branch — patch case/edit-owned
        // fields only and leave ClientName / ClientDob alone.
        setFormData((prev) => ({ ...prev, ...editPatch }));
        setInitEditFormData((prev) => ({
          ...editPatch,
          [CreateEditCaseSidebarFormDataFieldsName.ClientName]:
            prev?.[CreateEditCaseSidebarFormDataFieldsName.ClientName] ?? '',
          [CreateEditCaseSidebarFormDataFieldsName.ClientDob]:
            prev?.[CreateEditCaseSidebarFormDataFieldsName.ClientDob] ?? null,
        }));

        // No uploaded files in metadata, so set empty
        setUploadedFilesList(caseMetadata.allegationDocuments);
        // No status in metadata, so use default
        setCaseStatus(CasesFiltersStatusOptions.NEW);
        setIsEditMode(true);
      }
    }
  }, [caseId, caseMetadata, casesList, setFormData, usersList]);

  // Default the assignee to the current logged-in user on create only.
  // Two guards:
  //  1. `hasHydratedEditDataRef` — set by useLayoutEffect above the
  //     moment it pulls edit data from casesList/caseMetadata. Once
  //     true we must never overwrite the form, even if assignedUser
  //     ends up null (e.g. usersList hadn't loaded when the lookup ran;
  //     the layoutEffect will re-run and set it correctly later).
  //  2. Inline `isEditFlow` check — covers the case where
  //     casesList/caseMetadata are populated but the layoutEffect
  //     hasn't run yet on the current render. We never autopopulate
  //     when the data needed for hydration is already in the store.
  useEffect(() => {
    if (hasHydratedEditDataRef.current) return;
    if (hasUserClearedAssigneeRef.current) return;
    const isEditFlow = !!caseId && (!!casesList.find((c) => c.caseId === caseId) || !!caseMetadata);
    if (isEditFlow) return;
    if (formData[CreateEditCaseSidebarFormDataFieldsName.AssignedUser]) return;
    if (!currentUser || !usersList.length) return;
    const matched = usersList.find((u) => u.userId === currentUser.userId) ?? null;
    if (matched) {
      handleValueChange(CreateEditCaseSidebarFormDataFieldsName.AssignedUser, matched);
    }
  }, [caseId, casesList, caseMetadata, currentUser, usersList, formData, handleValueChange]);

  // FileList-shaped wrapper that mirrors the original handleFileChange:
  // requests an upload URL per file, kicks off the Azure upload, and
  // appends the new id onto formData[AllegationDocuments] so the form's
  // canSubmit / isAllFilesUploaded gates pick it up.
  const handleAllegationFilesSelected = (files: FileList) => {
    if (!files.length || !caseId) return;
    Array.from(files).forEach((file) => {
      const fileExtension = file.name.split('.').pop()?.toLocaleUpperCase();
      if (fileExtension !== 'DOC' && fileExtension !== 'DOCX' && fileExtension !== 'PDF') return;
      triggerPostCaseAllegationGetUpload({
        caseId,
        documentName: file.name,
        documentFormat: fileExtension,
        sizeMb: +(file.size / (1024 * 1024)),
        isEdit: isEditMode,
      })
        .unwrap()
        .then((query) => {
          const { uploadPath, uploadToken, allegationDocumentId } = query || {};
          if (uploadPath && uploadToken && allegationDocumentId) {
            uploadFile(allegationDocumentId, file, uploadPath, uploadToken);
            setFormData((prev) => ({
              ...prev,
              [CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]: [
                ...prev[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments],
                allegationDocumentId,
              ],
            }));
          }
        })
        .catch((error) => {
          if (isEndpointLockError(error)) return;
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Upload failed',
            detail: 'Failed to upload the document. Please try again.',
            life: 5000,
          });
        });
    });
  };

  // Single delete handler that mirrors handleDeleteFileClick +
  // handleDeleteUploadedFileClick. Always prunes uploadedFilesList too —
  // safe because filter on a non-matching id is a no-op.
  const handleAllegationDelete = (allegationDocumentId: string) => {
    if (!caseId) return;
    // If the file is still streaming to Azure, abort the upload and drop
    // the entry from the uploader before calling the backend delete —
    // otherwise the upload can finish after we've already deleted the
    // allegation, leaving an orphaned blob.
    const inFlight = uploadList.find((item) => item.id === allegationDocumentId);
    const hasUnfinishedUpload = !!inFlight && inFlight.status !== 'success';
    if (hasUnfinishedUpload) {
      cancelUpload(allegationDocumentId);
      deleteUpload(allegationDocumentId);
    }
    const removeAllegationDocument = () => {
      setFormData((prev) => ({
        ...prev,
        [CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]: prev[
          CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments
        ].filter((item) => item !== allegationDocumentId),
      }));
      setUploadedFilesList((prev) => prev.filter((f) => f.allegationDocumentId !== allegationDocumentId));
    };
    setPendingAllegationDeletes((count) => count + 1);
    triggerPostCaseAllegationDelete({ caseId, allegationDocumentId })
      .unwrap()
      .then(removeAllegationDocument)
      .catch((error) => {
        // Its upload was cancelled above and can't be resumed, so it goes
        // either way; left in, it would keep Confirm disabled with no tile.
        if (hasUnfinishedUpload) {
          removeAllegationDocument();
          return;
        }
        if (isEndpointLockError(error)) return;
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'Delete failed',
          detail: 'Failed to delete the document. Please try again.',
          life: 5000,
        });
      })
      .finally(() => setPendingAllegationDeletes((count) => count - 1));
  };

  // Download an allegation doc via the case-scoped signed-URL endpoint.
  // The endpoint returns nodes for every doc on the case; we pick the
  // matching id, hand the path + token to the shared Azure downloader
  // (same util used by uploaded-medical-records downloads). Toast on any
  // failure so a missing doc or expired signature doesn't fail silently.
  const handleAllegationDownload = (allegationDocumentId: string) => {
    if (!caseId) return;
    triggerGetCaseAllegationDownload({ caseId })
      .unwrap()
      .then((response) => {
        const match = response.nodes.find((n) => n.allegationDocumentId === allegationDocumentId);
        if (!match) {
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Download unavailable',
            detail: 'This document could not be located. Please refresh the page and try again.',
            life: 5000,
          });
          return;
        }
        downloadFile(match.allegationDocumentName, match.downloadPath, match.downloadToken);
      })
      .catch(() => {
        globalToastRef.current?.show({
          severity: 'error',
          summary: 'Download failed',
          detail: 'The document could not be downloaded. Please try again.',
          life: 5000,
        });
      });
  };

  const handleCaseReferenceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.toUpperCase();
    const isValid = allowedCaseReferenceInputSymbolsRegex.test(inputValue);

    if (inputValue.length <= 50 && isValid) {
      handleValueChange(CreateEditCaseSidebarFormDataFieldsName.CaseReference, inputValue);
    }
  };

  const handleRedirectToCasePage = () => {
    const { pathname } = location;
    const casePageUri = `/${AppRoutes.CaseList}/${caseId}`;

    if (!pathname.includes(casePageUri)) {
      navigate(casePageUri);
    }
  };

  const handleCreateClick = (e: React.MouseEvent<HTMLButtonElement>, hide: (event: React.SyntheticEvent) => void) => {
    const assignedTo = formData[CreateEditCaseSidebarFormDataFieldsName.AssignedUser]?.userId;
    const caseReference = formData[CreateEditCaseSidebarFormDataFieldsName.CaseReference];
    const dueDate = formData[CreateEditCaseSidebarFormDataFieldsName.DueDate];
    const isUrgent = formData[CreateEditCaseSidebarFormDataFieldsName.Urgent];
    const gRef = formData[CreateEditCaseSidebarFormDataFieldsName.GReference] || null;
    const paginationProduct = formData[CreateEditCaseSidebarFormDataFieldsName.PaginationProduct];
    const allegationDocumentIds = formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments];
    // Only send the instructing company id for MLS users — non-MLS users
    // never see the dropdown, so the form value is whatever it was
    // hydrated with (always null on create). Explicit guard keeps the
    // field out of the payload entirely for non-MLS orgs.
    const instructingCompanyId = isMlsUser
      ? formData[CreateEditCaseSidebarFormDataFieldsName.InstructingCompany]
      : null;

    if (caseId && paginationProduct && /*assignedTo &&*/ caseReference && dueDate) {
      // After case create succeeds, fire postCaseInfoEdit for the
      // optional client name / DOB if either is set. They live on a
      // separate endpoint (case/case-info/edit) and are intentionally
      // not part of the case-create payload.
      const clientName = formData[CreateEditCaseSidebarFormDataFieldsName.ClientName].trim();
      const clientDob = formData[CreateEditCaseSidebarFormDataFieldsName.ClientDob];
      triggerPostCaseCreate({
        caseId: caseId,
        assignedTo: assignedTo,
        caseReference: caseReference,
        dueDate: format(dueDate, 'yyyy-MM-dd'),
        isUrgent: isUrgent,
        gRef: gRef || null,
        paginationProduct: paginationProduct.code,
        allegationDocumentIds: allegationDocumentIds,
        instructingCompanyId: instructingCompanyId,
      })
        .unwrap()
        .then(async () => {
          if (clientName || clientDob) {
            try {
              await triggerPostCaseInfoEdit({
                caseId,
                ...(clientName ? { clientName } : {}),
                ...(clientDob ? { clientDob: format(clientDob, 'yyyy-MM-dd') } : {}),
              }).unwrap();
            } catch (error) {
              console.error('Failed to save client details on create:', error);
            }
          }
          handleRedirectToCasePage();

          if (formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]?.length) {
            formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments].forEach((uploadedFileId) => {
              const fileData = uploadList.find((item) => item.id === uploadedFileId);
              if (fileData) {
                deleteUpload(uploadedFileId);
              }
            });
          }

          hide(e);
        })
        .catch((error: { status?: number }) => {
          // 409 → backend rejects because another case already uses this
          // reference. Surface a clear error and leave the sidebar open
          // so the user can correct the reference and retry.
          if (error?.status === 409) {
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: 'A case with this reference already exists.',
              life: 5000,
            });
          }
        });
    }
  };

  const handleEditClick = (e: React.MouseEvent<HTMLButtonElement>, hide: (event: React.SyntheticEvent) => void) => {
    if (!initEditFormData || !caseId) return;

    // Prepare changed fields only (except caseId)
    const changed: Partial<CaseEditProps> = {};

    // Case Reference
    if (
      !deepEqual(
        formData[CreateEditCaseSidebarFormDataFieldsName.CaseReference],
        initEditFormData[CreateEditCaseSidebarFormDataFieldsName.CaseReference],
      )
    ) {
      changed.caseReference = formData[CreateEditCaseSidebarFormDataFieldsName.CaseReference];
    }
    // G Reference
    if (
      !deepEqual(
        formData[CreateEditCaseSidebarFormDataFieldsName.GReference],
        initEditFormData[CreateEditCaseSidebarFormDataFieldsName.GReference],
      )
    ) {
      changed.gRef = formData[CreateEditCaseSidebarFormDataFieldsName.GReference] || null;
    }
    // Assigned User. Send `null` (not `undefined`) when the user has been
    // cleared — caseEdit.ts strips undefined keys from the body, so an
    // unassign would otherwise never reach the backend.
    if (
      !deepEqual(
        formData[CreateEditCaseSidebarFormDataFieldsName.AssignedUser]?.userId,
        initEditFormData[CreateEditCaseSidebarFormDataFieldsName.AssignedUser]?.userId,
      )
    ) {
      changed.assignedTo = formData[CreateEditCaseSidebarFormDataFieldsName.AssignedUser]?.userId ?? null;
    }
    // Due Date
    if (
      !deepEqual(
        format(formData[CreateEditCaseSidebarFormDataFieldsName.DueDate], 'yyyy-MM-dd'),
        format(initEditFormData[CreateEditCaseSidebarFormDataFieldsName.DueDate], 'yyyy-MM-dd'),
      )
    ) {
      changed.dueDate = formData[CreateEditCaseSidebarFormDataFieldsName.DueDate]
        ? format(formData[CreateEditCaseSidebarFormDataFieldsName.DueDate], 'yyyy-MM-dd')
        : null;
    }
    // Urgent
    if (
      !deepEqual(
        formData[CreateEditCaseSidebarFormDataFieldsName.Urgent],
        initEditFormData[CreateEditCaseSidebarFormDataFieldsName.Urgent],
      )
    ) {
      changed.isUrgent = formData[CreateEditCaseSidebarFormDataFieldsName.Urgent];
    }
    // Pagination Product
    if (
      !deepEqual(
        formData[CreateEditCaseSidebarFormDataFieldsName.PaginationProduct]?.code,
        initEditFormData[CreateEditCaseSidebarFormDataFieldsName.PaginationProduct]?.code,
      )
    ) {
      changed.paginationProduct = formData[CreateEditCaseSidebarFormDataFieldsName.PaginationProduct]?.code;
    }
    // Allegation Documents
    if (
      !deepEqual(
        formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments],
        initEditFormData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments],
      )
    ) {
      changed.allegationDocumentIds = formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments];
    }
    // Instructing Company (MLS only). Send `null` (not `undefined`) when
    // the user has cleared the dropdown — caseEdit.ts strips undefined
    // keys from the body, so an unset would otherwise never reach the
    // backend. Skip entirely for non-MLS users so a stale form value
    // can't leak into the payload.
    if (
      isMlsUser &&
      !deepEqual(
        formData[CreateEditCaseSidebarFormDataFieldsName.InstructingCompany],
        initEditFormData[CreateEditCaseSidebarFormDataFieldsName.InstructingCompany],
      )
    ) {
      changed.instructingCompanyId = formData[CreateEditCaseSidebarFormDataFieldsName.InstructingCompany] ?? null;
    }

    // Always send caseId
    const payload: CaseEditProps = {
      caseId: caseId,
      ...changed,
    };

    // Detect changes to the clientName / clientDob optional fields,
    // which go through case/case-info/edit (not case/edit). We fire
    // both endpoints in parallel — same split as
    // TimelineChronologyPage's ActionsPanel.handleSaveDetails.
    const trimmedClientName = formData[CreateEditCaseSidebarFormDataFieldsName.ClientName].trim();
    const initialClientName = initEditFormData[CreateEditCaseSidebarFormDataFieldsName.ClientName].trim();
    const newDob = formData[CreateEditCaseSidebarFormDataFieldsName.ClientDob];
    const initialDob = initEditFormData[CreateEditCaseSidebarFormDataFieldsName.ClientDob];
    const newDobIso = newDob ? format(newDob, 'yyyy-MM-dd') : null;
    const initialDobIso = initialDob ? format(initialDob, 'yyyy-MM-dd') : null;
    const clientNameChanged = trimmedClientName !== initialClientName;
    const clientDobChanged = newDobIso !== initialDobIso;

    // If absolutely nothing changed (case/edit fields *and* the
    // additionalInfo fields), close without firing anything.
    const changedKeys = Object.keys(changed);
    if (changedKeys.length === 0 && !clientNameChanged && !clientDobChanged) {
      hide(e);
      return;
    }

    const requests: Promise<unknown>[] = [];
    if (changedKeys.length > 0) {
      requests.push(triggerPostCaseEdit(payload).unwrap());
    }
    if (clientNameChanged || clientDobChanged) {
      requests.push(
        triggerPostCaseInfoEdit({
          caseId,
          ...(clientNameChanged ? { clientName: trimmedClientName } : {}),
          ...(clientDobChanged ? { clientDob: newDobIso } : {}),
        }).unwrap(),
      );
    }

    Promise.all(requests)
      .then(() => {
        // Snapshot the just-saved values back into initEditFormData so a
        // second Save click without further edits is a no-op (the diff
        // detector compares against this baseline). The layoutEffect
        // already re-syncs case/edit-owned fields, but ClientName /
        // ClientDob aren't part of casesList — without this they'd
        // remain at their pre-save values and re-fire on the next click.
        setInitEditFormData((prev) =>
          prev
            ? {
                ...prev,
                [CreateEditCaseSidebarFormDataFieldsName.ClientName]: trimmedClientName,
                [CreateEditCaseSidebarFormDataFieldsName.ClientDob]: newDob,
                // Snapshot the just-saved instructing company id so a
                // second Save click without further edits is a no-op.
                [CreateEditCaseSidebarFormDataFieldsName.InstructingCompany]:
                  formData[CreateEditCaseSidebarFormDataFieldsName.InstructingCompany],
              }
            : prev,
        );
        // Edit flow stays on the current page (often the case list).
        // Only the create flow redirects to the new case's detail page.
        if (formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]?.length) {
          formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments].forEach((uploadedFileId) => {
            const fileData = uploadList.find((item) => item.id === uploadedFileId);
            if (fileData) {
              deleteUpload(uploadedFileId);
            }
          });
        }

        hide(e);
      })
      .catch((error: { status?: number }) => {
        // Checked first: a refusal while the case is being cloned is a
        // 409 too, and the lock has already shown its own toast. The
        // sidebar stays open so the user can save once it has finished.
        if (isEndpointLockError(error)) return;
        // 409 → backend rejects the new caseReference because another
        // case already uses it. Same handling as create — surface a
        // clear toast and leave the sidebar open for retry.
        if (error?.status === 409) {
          globalToastRef.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'A case with this reference already exists.',
            life: 5000,
          });
        }
      });
  };

  const handleCancelClick = () => {
    if (caseId) {
      triggerPostCaseCreateCancel({ caseId });
    }
    if (formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]?.length) {
      formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments].forEach((uploadedFileId) => {
        const fileData = uploadList.find((item) => item.id === uploadedFileId);
        if (caseId && fileData) {
          // deleteFile(uploadedFileId);
          triggerPostCaseAllegationDelete({
            caseId: caseId,
            allegationDocumentId: uploadedFileId,
          });
        }
      });
    }
  };

  const isAllFilesUploaded = useMemo(() => {
    return formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments]?.every((uploadedFileId) => {
      /** If uploadedFileId is present in uploadedFilesList, consider it uploaded */
      const isInUploadedFilesList = uploadedFilesList?.some((file) => file.allegationDocumentId === uploadedFileId);
      if (isInUploadedFilesList) {
        return true;
      }

      /** Otherwise, check uploadList status */
      const fileData = uploadList.find((item) => item.id === uploadedFileId);
      return fileData && fileData.status === 'success';
    });
  }, [formData, uploadList, uploadedFilesList]);

  if (!usersList) {
    return null;
  }

  // Allowed statuses for product type editing
  const allowedStatusesForProductType: string[] = [
    CasesFiltersStatusOptions.NEW,
    CasesFiltersStatusOptions.DOCUMENT_UPLOAD,
    CasesFiltersStatusOptions.READY_FOR_AI_PROCESSING,
    CasesFiltersStatusOptions.WAITING_FOR_DOCUMENT_PROCESSING,
  ];

  return (
    <Sidebar
      visible
      closeOnEscape={false}
      dismissable={false}
      position="right"
      onHide={closeSidebar}
      // PrimeReact writes the mask z-index *inline* via ZIndexUtils after
      // mount, and bumps it for every subsequent overlay (modal/toast/etc.)
      // because it shares one global queue. Plain Tailwind classes lose to
      // inline styles, so we need `!important` (the `!` prefix on
      // `!z-[1000]`) — that does beat inline styles, since PrimeReact's
      // own inline write isn't marked important. Pin to 1000 so the
      // CaseTemplate header wrapper at `z-[1102]` always sits in front,
      // no matter how many opens / closes happen this session.
      // Also push the mask below the 48px nav so its dimming layer can't
      // cover the header. The panel is `height: 100%` of the mask, so
      // shrinking the mask shrinks the panel automatically. Don't style
      // `root` — `.p-sidebar` is `position: relative` inside the mask, so
      // a `top` there would push the panel further down within it.
      pt={{
        mask: {
          className: '!z-[1000]',
          style: { top: NAV_HEIGHT_PX, height: `calc(100% - ${NAV_HEIGHT_PX}px)` },
        },
      }}
      // The atomic-components Sidebar theme applies `border-0 shadow-lg`
      // on the root, so `!border-l` overrides the zero border and
      // SIDEBAR_PANEL_SHADOW replaces the default `shadow-lg` (which is
      // more drop-style and centred under the panel) with a wider, softer
      // wash leftward from the panel edge. `border-fletchers-gray-110/55`
      // matches the nav's bottom-border alpha (rgba(225,225,224,0.55) in
      // Header.tsx) so the sidebar's left edge and the nav's bottom edge
      // form a continuous hairline at exactly the same hue.
      className={classNames(
        '!border-fletchers-gray-110/55 !w-[412px] max-w-[412px] min-w-[412px] !border-l',
        SIDEBAR_PANEL_SHADOW,
      )}
      content={({ closeIconRef, hide }) => (
        // Outer wrapper has 0 right padding so the scrollbar sits flush
        // against the sidebar's right edge. The footer keeps a 24px
        // right padding (`pr-6`) so the buttons line up with the rest
        // of the sidebar's outer rhythm; the scroll container uses 14px
        // (`pr-[14px]`) so its content has slightly tighter breathing
        // room next to the scrollbar. Title and the 24px top padding
        // both sit *inside* the scroll container so they scroll away
        // as the user moves down.
        <div className="flex h-full flex-col pr-0 pb-4 pl-6">
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto pt-6 pr-6">
            <div className="flex flex-shrink-0 text-[20px]/[28px] font-[600]">
              {isEditMode ? 'Edit case' : 'Create case'}
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="case-reference" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                Case reference
              </label>
              <InputText
                id="case-reference"
                aria-describedby="case reference"
                value={formData[CreateEditCaseSidebarFormDataFieldsName.CaseReference]}
                onChange={handleCaseReferenceChange}
                // The PrimeReact InputText theme uses a CSS :has(~&) selector
                // to colour the *parent* `text-black` on focus (intended for
                // icon fields, but it bleeds onto sibling labels). Labels
                // use `!text-fletchers-gray-200` to ignore that inheritance.
                // The border colour overrides use Tailwind v4's `class!`
                // suffix form, which generates a true `!important` and beats
                // the theme's `focus:border-fletchers-gray-200` rule.
                className="border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! text-[14px]/[20px]! focus:outline-none!"
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5">
                <label htmlFor="product-type" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                  Product type
                </label>
                <InfoTooltipIcon tooltip="Select how you'd like to AI process this case." />
              </div>
              <Dropdown
                id="product-type"
                options={productTypeOptions}
                value={formData[CreateEditCaseSidebarFormDataFieldsName.PaginationProduct]}
                onChange={(event: DropdownChangeEvent) =>
                  handleValueChange(CreateEditCaseSidebarFormDataFieldsName.PaginationProduct, event.value)
                }
                dataKey="code"
                optionLabel="name"
                {...themedDropdownPt()}
                disabled={!allowedStatusesForProductType.includes(caseStatus)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">Assigned to</label>
              {(() => {
                const assignedUser = formData[CreateEditCaseSidebarFormDataFieldsName.AssignedUser];
                // On hover, swap the avatar for a grey circle with a pencil
                // icon to signal the row is clickable / editable. Matches the
                // V2 CaseInfoSidebar UserField pattern.
                const avatar = isAssignUserHovered ? (
                  <div className="bg-fletchers-gray-110 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
                    <PencilIcon className="text-fletchers-black-900 h-3 w-3" />
                  </div>
                ) : assignedUser ? (
                  <UserAvatar name={assignedUser.userName} size={24} fontSize={10} />
                ) : (
                  <UserAvatar unassigned size={24} />
                );
                return (
                  <div className="flex h-8 items-center gap-2">
                    <button
                      type="button"
                      ref={assignUserTriggerRef}
                      onClick={handleOpenAssignUserPopup}
                      onMouseEnter={() => setIsAssignUserHovered(true)}
                      onMouseLeave={() => setIsAssignUserHovered(false)}
                      title={assignedUser?.userName ?? 'Assign user'}
                      className="flex h-8 cursor-pointer items-center gap-2 rounded-[4px] text-left transition-colors"
                    >
                      {avatar}
                      {assignedUser ? (
                        <span className="text-fletchers-black-900 truncate text-[14px]/[20px]">
                          {assignedUser.userName}
                        </span>
                      ) : (
                        <span className="text-fletchers-gray-200 text-[14px]/[20px] italic">Unassigned</span>
                      )}
                    </button>
                    {assignedUser && (
                      <ToolbarChipButton
                        aria-label="Clear assigned user"
                        title="Clear assigned user"
                        onClick={() => {
                          hasUserClearedAssigneeRef.current = true;
                          handleValueChange(CreateEditCaseSidebarFormDataFieldsName.AssignedUser, null);
                        }}
                      >
                        <CloseIcon width={10} height={10} />
                      </ToolbarChipButton>
                    )}
                  </div>
                );
              })()}
              {assignUserAnchor && (
                <AssignUserPopup
                  anchor={assignUserAnchor}
                  usersList={usersList}
                  currentUserId={formData[CreateEditCaseSidebarFormDataFieldsName.AssignedUser]?.userId ?? null}
                  onSelect={(user) => handleValueChange(CreateEditCaseSidebarFormDataFieldsName.AssignedUser, user)}
                  onClose={handleCloseAssignUserPopup}
                />
              )}
            </div>

            {/* ── Case documents section ── */}
            {(() => {
              // Build the unified document list from two sources:
              //  • `uploadedFilesList` — edit mode: persisted allegation
              //    docs that came back from the case fetch.
              //  • `uploadList` — in-flight uploads from the file
              //    uploader; their progress drives the tile's overlay.
              // We dedupe by id so a doc that's both persisted *and*
              // mid-upload doesn't render twice.
              const allegationIds = formData[CreateEditCaseSidebarFormDataFieldsName.AllegationDocuments] ?? [];
              const seen = new Set<string>();
              const documents: CaseBackgroundDocItem[] = [];
              uploadedFilesList?.forEach((file) => {
                if (!file.allegationDocumentId || !file.allegationDocumentName) return;
                if (seen.has(file.allegationDocumentId)) return;
                seen.add(file.allegationDocumentId);
                documents.push({ id: file.allegationDocumentId, name: file.allegationDocumentName });
              });
              allegationIds.forEach((id) => {
                if (seen.has(id)) return;
                const inFlight = uploadList.find((item) => item.id === id);
                if (!inFlight) return;
                seen.add(id);
                documents.push({
                  id,
                  name: inFlight.file.name,
                  uploadProgress: inFlight.status === 'uploading' ? inFlight.progress : undefined,
                });
              });

              const canEdit =
                caseStatus === CasesFiltersStatusOptions.NEW ||
                caseStatus === CasesFiltersStatusOptions.DOCUMENT_UPLOAD;

              return (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <h2 className="text-fletchers-gray-200 text-[14px]/[20px] font-[500]">Case background</h2>
                    <InfoTooltipIcon
                      tooltip={
                        "Upload documents that contain information on the nature of the case and any specific questions you'd like Mermaidd to focus on during AI processing."
                      }
                    />
                  </div>
                  <CaseBackgroundSection
                    documents={documents}
                    canEdit={canEdit}
                    onUploadClick={() => {}}
                    onDelete={handleAllegationDelete}
                    onFilesSelected={handleAllegationFilesSelected}
                    onDownload={handleAllegationDownload}
                  />
                </div>
              );
            })()}

            <div className="border-fletchers-gray-110 border-t" />

            {/* Advanced fields accordion. Collapsed by default; clicking
                the header toggles `isAdvancedFieldsOpen` and rotates the
                chevron 180° via `rotate-180`. Three optional fields live
                inside: Additional reference, Due date, Urgent. */}
            <div className="mb-6 flex flex-col">
              <button
                type="button"
                onClick={() => setIsAdvancedFieldsOpen((open) => !open)}
                aria-expanded={isAdvancedFieldsOpen}
                aria-controls="advanced-fields"
                className="flex cursor-pointer items-center justify-between text-left"
              >
                <span className="!text-fletchers-gray-200 text-[14px]/[20px] font-[500]">Optional fields</span>
                <ChevronIcon
                  className={classNames('transition-transform duration-200', '[&_path]:!stroke-fletchers-gray-200', {
                    'rotate-180': isAdvancedFieldsOpen,
                  })}
                />
              </button>
              {isAdvancedFieldsOpen && <div className="border-fletchers-gray-110 mt-6 border-t" />}
              {isAdvancedFieldsOpen && (
                <div id="advanced-fields" className="mt-6 flex flex-col gap-6">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-1.5">
                      <label
                        htmlFor="g-reference"
                        className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]"
                      >
                        Additional reference
                      </label>
                      <InfoTooltipIcon tooltip="Add a secondary reference for this case." />
                    </div>
                    <InputText
                      id="g-reference"
                      aria-describedby="additional reference"
                      value={formData[CreateEditCaseSidebarFormDataFieldsName.GReference]}
                      onChange={(e) =>
                        handleValueChange(CreateEditCaseSidebarFormDataFieldsName.GReference, e.target.value)
                      }
                      className="border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! text-[14px]/[20px]! focus:outline-none!"
                    />
                  </div>

                  {/* Instructing company (MLS org only — non-MLS users
                      never see this field). Single-select dropdown
                      populated from organisation/list-instructing-companies;
                      `showClear` renders the built-in PrimeReact "x"
                      affordance that clears the selection back to null.
                      Stores the whole company object as the dropdown's
                      value (matching the Product-type dropdown above —
                      PrimeReact's `optionValue` doesn't reliably round-
                      trip selection when combined with `showClear`), and
                      we extract the id at submit-time / on change. */}
                  {isMlsUser && (
                    <div className="flex flex-col gap-2">
                      <label
                        htmlFor="instructing-company"
                        className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]"
                      >
                        Instructing company
                      </label>
                      <Dropdown
                        id="instructing-company"
                        options={mergedInstructingCompanies}
                        value={
                          mergedInstructingCompanies.find(
                            (c) =>
                              c.instructingCompanyId ===
                              formData[CreateEditCaseSidebarFormDataFieldsName.InstructingCompany],
                          ) ?? null
                        }
                        onChange={(event: DropdownChangeEvent) => {
                          const selected = event.value as InstructingCompany | null;
                          handleValueChange(
                            CreateEditCaseSidebarFormDataFieldsName.InstructingCompany,
                            selected?.instructingCompanyId ?? null,
                          );
                        }}
                        dataKey="instructingCompanyId"
                        optionLabel="name"
                        placeholder="Select instructing company"
                        showClear
                        // Mirror the Product-type dropdown's chevron + panel
                        // overrides so the trigger and open panel look
                        // identical to the rest of the sidebar's dropdowns.
                        dropdownIcon={<ChevronIcon className="[&_path]:!stroke-fletchers-gray-200" />}
                        collapseIcon={<ChevronIcon className="[&_path]:!stroke-fletchers-gray-200 rotate-180" />}
                        pt={{
                          root: {
                            className:
                              '!h-8 !border-fletchers-gray-110 hover:!border-fletchers-gray-110 !outline-none !outline-offset-0',
                          },
                          input: { className: '!text-[14px]/[20px] !py-0 !pl-2 flex !items-center' },
                          panel: {
                            className:
                              'border border-fletchers-gray-110 !rounded-[4px] overflow-hidden translate-y-[4px] shadow-[0_4px_16px_rgba(0,0,0,0.12)]',
                          },
                          wrapper: { className: '!mt-0' },
                          item: ({ context }: { context: { selected: boolean; focused: boolean } }) => ({
                            className: classNames('!pl-2', {
                              '!bg-fletchers-green-50 hover:!bg-fletchers-green-50': context.selected,
                              '!bg-fletchers-gray-50 hover:!bg-fletchers-gray-50': !context.selected && context.focused,
                            }),
                          }),
                        }}
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="client-name"
                      className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]"
                    >
                      Client name
                    </label>
                    <InputText
                      id="client-name"
                      value={formData[CreateEditCaseSidebarFormDataFieldsName.ClientName]}
                      onChange={(e) =>
                        handleValueChange(CreateEditCaseSidebarFormDataFieldsName.ClientName, e.target.value)
                      }
                      className="border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! text-[14px]/[20px]! focus:outline-none!"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="client-dob"
                      className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]"
                    >
                      Client date of birth
                    </label>
                    <ConfiguredCalendar
                      id="client-dob"
                      value={formData[CreateEditCaseSidebarFormDataFieldsName.ClientDob]}
                      selectionMode="single"
                      hideOnRangeSelection
                      // `appendTo="self"` keeps the popup as a child of
                      // the calendar wrapper so we can size it relative
                      // to the input (instead of having PrimeReact's JS
                      // align it via `top`/`left` on document.body).
                      appendTo="self"
                      // `!w-full` snaps the panel to the field width;
                      // `!left-0` un-centres it. Open *above* the field
                      // (`bottom: calc(100% + 4px)`) since the calendars
                      // sit at the bottom of the sidebar — opening below
                      // would clip / require scrolling. `!top-auto`
                      // overrides PrimeReact's default `top: 0`.
                      panelClassName="!w-full !left-0 !top-auto !bottom-[calc(100%+4px)]"
                      onChange={(e) =>
                        handleValueChange(CreateEditCaseSidebarFormDataFieldsName.ClientDob, (e.value as Date) ?? null)
                      }
                      className={classNames(
                        'w-full',
                        '[&>input]:!h-8 [&>input]:!py-0 [&>input]:!text-[14px]/[20px]',
                        // Bump the placeholder ('dd/mm/yyyy') from the
                        // InputText theme's default 12px up to 14px so
                        // it matches the value text.
                        '[&>input]:placeholder:!text-[14px]',
                        // Pin the input border to gray-110 across rest /
                        // hover / focus / open. The Calendar theme tints
                        // it green via :has() selectors on hover and on
                        // overlayVisible — `!important` beats both.
                        '[&>input]:!border-fletchers-gray-110 [&>input:hover]:!border-fletchers-gray-110 [&>input:focus]:!border-fletchers-gray-110',
                        // Same treatment for the icon button next to the
                        // input — the theme also drives this to green on
                        // hover / focus / open.
                        '[&>button]:!border-fletchers-gray-110 [&>button:hover]:!border-fletchers-gray-110 [&>button:focus]:!border-fletchers-gray-110',
                        // Calendar icon: gray-200 at rest, green-600 on
                        // hover (regardless of whether the input or the
                        // button itself is hovered — the theme paints it
                        // green via :has() selectors on the wrapper).
                        '[&>button>svg>path]:!fill-fletchers-gray-200',
                        '[&:hover>button>svg>path]:!fill-fletchers-green-600',
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label htmlFor="due-date" className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">
                      Due date
                    </label>
                    <ConfiguredCalendar
                      id="due-date"
                      value={formData[CreateEditCaseSidebarFormDataFieldsName.DueDate]}
                      selectionMode="single"
                      hideOnRangeSelection
                      // `appendTo="self"` keeps the popup as a child of
                      // the calendar wrapper so we can size it relative
                      // to the input (instead of having PrimeReact's JS
                      // align it via `top`/`left` on document.body).
                      appendTo="self"
                      // `!w-full` snaps the panel to the field width;
                      // `!left-0` un-centres it. Open *above* the field
                      // (`bottom: calc(100% + 4px)`) since the calendars
                      // sit at the bottom of the sidebar — opening below
                      // would clip / require scrolling. `!top-auto`
                      // overrides PrimeReact's default `top: 0`.
                      panelClassName="!w-full !left-0 !top-auto !bottom-[calc(100%+4px)]"
                      onChange={(e) =>
                        handleValueChange(CreateEditCaseSidebarFormDataFieldsName.DueDate, e.value as Date)
                      }
                      className={classNames(
                        'w-full',
                        '[&>input]:!h-8 [&>input]:!py-0 [&>input]:!text-[14px]/[20px]',
                        // Bump the placeholder ('dd/mm/yyyy') from the
                        // InputText theme's default 12px up to 14px so
                        // it matches the value text.
                        '[&>input]:placeholder:!text-[14px]',
                        // Pin the input border to gray-110 across rest /
                        // hover / focus / open. The Calendar theme tints
                        // it green via :has() selectors on hover and on
                        // overlayVisible — `!important` beats both.
                        '[&>input]:!border-fletchers-gray-110 [&>input:hover]:!border-fletchers-gray-110 [&>input:focus]:!border-fletchers-gray-110',
                        // Same treatment for the icon button next to the
                        // input — the theme also drives this to green on
                        // hover / focus / open.
                        '[&>button]:!border-fletchers-gray-110 [&>button:hover]:!border-fletchers-gray-110 [&>button:focus]:!border-fletchers-gray-110',
                        // Calendar icon: gray-200 at rest, green-600 on
                        // hover (regardless of whether the input or the
                        // button itself is hovered — the theme paints it
                        // green via :has() selectors on the wrapper).
                        '[&>button>svg>path]:!fill-fletchers-gray-200',
                        '[&:hover>button>svg>path]:!fill-fletchers-green-600',
                      )}
                    />
                  </div>

                  <div className="flex flex-row items-center gap-1.5">
                    <Checkbox
                      inputId="urgent"
                      type="checkbox"
                      checked={formData[CreateEditCaseSidebarFormDataFieldsName.Urgent]}
                      onChange={(event) =>
                        handleValueChange(CreateEditCaseSidebarFormDataFieldsName.Urgent, event.checked ?? false)
                      }
                    />
                    <span className="!text-fletchers-gray-200 block text-[14px]/[20px] font-[500]">Urgent</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Hairline divider above the action buttons. `border-t` lives
              on the wrapper itself; `pt-4` gives a 16px gap between the
              divider line and the buttons below. `mr-6` shifts the row's
              right edge inward by 24px so the border stops short of the
              panel edge — matching the buttons' own 24px right margin
              against the panel edge. `mt-auto` keeps the row pinned to
              the bottom of the flex column. */}
          <div className="border-fletchers-gray-110 mt-auto mr-6 flex flex-row items-center justify-end gap-2 border-t pt-4">
            <OutlineButton
              ref={closeIconRef as RefObject<Button | null>}
              onClick={(e) => {
                if (!isEditMode) {
                  handleCancelClick();
                }
                hide(e);
              }}
            >
              Cancel
            </OutlineButton>
            {/* A document being deleted stays in the form until the delete returns, so saving waits for it */}
            <GreenButton
              disabled={!canSubmit || !isAllFilesUploaded || pendingAllegationDeletes > 0}
              onClick={(e) => (isEditMode ? handleEditClick(e, hide) : handleCreateClick(e, hide))}
            >
              Confirm
            </GreenButton>
          </div>
        </div>
      )}
    />
  );
};
